import { useEffect, useRef } from 'react';

// Color presets for different pages
export const PARTICLE_COLORS = {
  teal: { primary: '35, 247, 221', secondary: '112, 255, 239' },      // Login - brand color
  purple: { primary: '147, 51, 234', secondary: '192, 132, 252' },    // Register
  gold: { primary: '251, 191, 36', secondary: '253, 224, 71' },       // Homepage
  blue: { primary: '59, 130, 246', secondary: '147, 197, 253' },      // Dashboard
  green: { primary: '34, 197, 94', secondary: '134, 239, 172' },      // Webshop
  red: { primary: '239, 68, 68', secondary: '252, 165, 165' },        // Admin
  pink: { primary: '236, 72, 153', secondary: '249, 168, 212' },      // Projects
  orange: { primary: '249, 115, 22', secondary: '253, 186, 116' },    // Workers
  cyan: { primary: '6, 182, 212', secondary: '103, 232, 249' },       // Clients
  indigo: { primary: '99, 102, 241', secondary: '165, 180, 252' },    // Rentals
};

const ParticleXBackground = ({ 
  className = "", 
  opacity = 0.5, 
  particleCount = 1000,
  color = "teal" // default to brand color
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    // Get color values
    const colorScheme = PARTICLE_COLORS[color] || PARTICLE_COLORS.teal;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resizeCanvas();

    // Particle class
    class Particle {
      constructor(x, y, targetX, targetY) {
        this.x = x;
        this.y = y;
        this.targetX = targetX;
        this.targetY = targetY;
        this.size = Math.random() * 2 + 0.5;
        this.baseSize = this.size;
        this.vx = 0;
        this.vy = 0;
        this.friction = 0.92;
        this.spring = 0.015 + Math.random() * 0.015;
        this.brightness = Math.random() * 0.6 + 0.2;
        this.baseBrightness = this.brightness;
        this.phase = Math.random() * Math.PI * 2;
        this.pulseSpeed = 0.005 + Math.random() * 0.01;
        this.isPrimary = Math.random() > 0.3; // 70% primary color, 30% secondary
      }

      update(time) {
        // Move towards target with spring physics
        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        
        this.vx += dx * this.spring;
        this.vy += dy * this.spring;
        
        this.vx *= this.friction;
        this.vy *= this.friction;
        
        this.x += this.vx;
        this.y += this.vy;
        
        // Subtle floating effect
        this.x += Math.sin(time * 0.0008 + this.phase) * 0.3;
        this.y += Math.cos(time * 0.0008 + this.phase) * 0.3;
        
        // Pulse effect
        this.brightness = this.baseBrightness + Math.sin(time * this.pulseSpeed + this.phase) * 0.2;
        this.size = this.baseSize + Math.sin(time * this.pulseSpeed * 0.5 + this.phase) * 0.5;
      }

      draw(ctx) {
        const rgb = this.isPrimary ? colorScheme.primary : colorScheme.secondary;
        
        // Main particle
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, ${this.brightness})`;
        ctx.fill();
        
        // Glow effect
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size * 4
        );
        gradient.addColorStop(0, `rgba(${rgb}, ${this.brightness * 0.4})`);
        gradient.addColorStop(0.5, `rgba(${rgb}, ${this.brightness * 0.1})`);
        gradient.addColorStop(1, `rgba(${rgb}, 0)`);
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 4, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }

    // Create X shape particles
    const createParticles = () => {
      particles = [];
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      const centerX = width / 2;
      const centerY = height / 2;
      
      // X dimensions - make it proportional to screen
      const xSize = Math.min(width, height) * 0.35;
      const lineThickness = 80;
      
      for (let i = 0; i < particleCount; i++) {
        // Random position along one of the two diagonal lines of X
        const line = Math.random() > 0.5 ? 1 : -1;
        const t = (Math.random() - 0.5) * 2; // -1 to 1
        
        // Position along the diagonal
        const baseX = centerX + t * xSize;
        const baseY = centerY + t * xSize * line;
        
        // Add some width/thickness to the line with gaussian-like distribution
        const spread = (Math.random() + Math.random() + Math.random()) / 3;
        const offsetX = (Math.random() - 0.5) * lineThickness * spread;
        const offsetY = (Math.random() - 0.5) * lineThickness * spread;
        
        const targetX = baseX + offsetX;
        const targetY = baseY + offsetY;
        
        // Start from random position for assembly animation
        const startX = Math.random() * width;
        const startY = Math.random() * height;
        
        particles.push(new Particle(startX, startY, targetX, targetY));
      }
    };

    createParticles();

    // Animation loop
    let startTime = Date.now();
    const animate = () => {
      const time = Date.now() - startTime;
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      
      ctx.clearRect(0, 0, width, height);
      
      // Draw particles
      particles.forEach(particle => {
        particle.update(time);
        particle.draw(ctx);
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Recreate particles on resize
    const handleResize = () => {
      resizeCanvas();
      createParticles();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [particleCount, color]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ opacity }}
    />
  );
};

export default ParticleXBackground;
